import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ReactSVG } from "react-svg";
import Button from "../../../../common/Button";
import NewToolTip from "../../../../common/New-ToolTip";
import { formatDateToDD_MMM_YYYY } from "../../../../../helpers/functions";
import { Menu, Transition } from "@headlessui/react";
import MedicationCreate from "../../../clinical-services/service-steps/clinical-assessment/MedicationCreate";
import {
  updateInterventionAssessment,
  getInterventionById,
} from "../../../../../redux/modules/intervention/actions";
import { InterventionStepsEnum } from "../../../text/data";
import { useStepNavigation } from "./useStepNavigation";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import InterventionForm from "../InterventionForm";
import { DocumentationFormsCard } from "../components";
import {
  InterventionFormTypes,
  InterventionStatus,
  isAnnualMedicationReviewIntervention,
} from "../data";

const Documentation = ({ intervention, currentInterventionSteps, currentStep: activeStep, onNavigate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { interventionId } = useParams();
  const { nextStep, stepNumberOf } = useStepNavigation(
    InterventionStepsEnum.Documentation,
    currentInterventionSteps,
    activeStep
  );

  const [isEditing, setIsEditing] = useState(false);
  const [showMedicationSlider, setShowMedicationSlider] = useState(false);
  const [clickedMedication, setClickedMedication] = useState(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [comingSoonTooltipOpen, setComingSoonTooltipOpen] = useState(false);
  const [medicationCardEditSession, setMedicationCardEditSession] = useState(false);
  const [forceStayOnMedicationCard, setForceStayOnMedicationCard] = useState(false);

  const { medications, clinicalServiceObj: clinicalServiceObjFromClinical } =
    useSelector((state) => state.clinicalService || {});
  const { clinicalServiceObj: clinicalServiceObjFromIntervention } =
    useSelector((state) => state.intervention || {});
  const clinicalServiceObj =
    clinicalServiceObjFromIntervention || clinicalServiceObjFromClinical;
  const { patient } = useSelector((state) => state.patients || {});
  const comingSoonReturnedFormTooltipId = `documentation-returned-form-coming-soon-${interventionId ?? "unknown"}`;

  const formRoot =
    intervention?.interventionAssessment?.interventionAssessmentForm || {};
  const lastModified =
    formRoot?.lastModifiedDate || intervention?.createdDate || new Date();

  const [releasedToDocumentationForms, setReleasedToDocumentationForms] =
    useState(false);

  useEffect(() => {
    setReleasedToDocumentationForms(
      formRoot?.documentation?.releasedToDocumentationForms === true
    );
  }, [formRoot?.documentation?.releasedToDocumentationForms]);

  useEffect(() => {
    if (location?.state?.forceDocumentationForms) {
      setReleasedToDocumentationForms(true);
    }
  }, [location?.state?.forceDocumentationForms]);

  const interventionMedicationId =
    formRoot?.interventionDetails?.medicationId ||
    intervention?.medicationId ||
    null;

  const currentMedication =
    !interventionMedicationId || !medications
      ? null
      : medications.find((med) => med.id === interventionMedicationId) || null;

  const medicationDetails = formRoot?.interventionDetails || {};
  let medicationRecordForUpdate = null;
  if (interventionMedicationId && currentMedication) {
    medicationRecordForUpdate = currentMedication;
  } else {
    const name =
      medicationDetails?.medicationName &&
      medicationDetails.medicationName !== "[Name of Drug]"
        ? medicationDetails.medicationName
        : intervention?.medicationName || "";

    const hasFallbackData = Boolean(
      interventionMedicationId ||
        name ||
        medicationDetails?.dosage ||
        medicationDetails?.form ||
        medicationDetails?.route ||
        medicationDetails?.frequency
    );

    if (hasFallbackData) {
      medicationRecordForUpdate = {
        id: interventionMedicationId || null,
        medicationName: name,
        dosage: medicationDetails?.dosage,
        form: medicationDetails?.form,
        route: medicationDetails?.route,
        frequency: medicationDetails?.frequency,
      };
    }
  }

  const isMedicationUpdateComplete =
    formRoot?.documentation?.completeMedicationUpdate === true;

  const medSavedSessionKey = interventionId
    ? `inv-doc-med-saved-${interventionId}`
    : null;
  const finalDocumentationSessionKey = interventionId
    ? `inv-doc-from-billing-final-${interventionId}`
    : null;
  const profileAnalysisSessionKey = interventionId
    ? `inv-doc-profile-analysis-${interventionId}`
    : null;

  const [medicationRecordSavedViaSlider, setMedicationRecordSavedViaSlider] = useState(false);
  const [openedFromBillingFinalDocumentation, setOpenedFromBillingFinalDocumentation] = useState(false);
  const [showCompletedInterventionState, setShowCompletedInterventionState] = useState(false);
  const [profileAnalysispopup, setProfileAnalysispopup] = useState(false);
  const [profileAnalysisStatus, setProfileAnalysisStatus] = useState("pending");
  const isSessionStorageAvailable = typeof sessionStorage !== "undefined";

  const getSessionFlag = (key) => {
    if (!key || !isSessionStorageAvailable) return false;
    return sessionStorage.getItem(key) === "1";
  };

  const setSessionFlag = (key, value) => {
    if (!key || !isSessionStorageAvailable) return;
    if (value) {
      sessionStorage.setItem(key, "1");
      return;
    }
    sessionStorage.removeItem(key);
  };

  useEffect(() => {
    setMedicationRecordSavedViaSlider(getSessionFlag(medSavedSessionKey));
  }, [medSavedSessionKey, isSessionStorageAvailable]);

  useEffect(() => {
    setOpenedFromBillingFinalDocumentation(
      getSessionFlag(finalDocumentationSessionKey)
    );
  }, [finalDocumentationSessionKey, isSessionStorageAvailable]);

  useEffect(() => {
    setProfileAnalysisStatus(
      getSessionFlag(profileAnalysisSessionKey) ? "completed" : "pending"
    );
  }, [profileAnalysisSessionKey, isSessionStorageAvailable]);

  useEffect(() => {
    const openedFromBillingFinalDoc = getSessionFlag(finalDocumentationSessionKey);
    setShowCompletedInterventionState(
      intervention?.status === InterventionStatus.Completed &&
        !openedFromBillingFinalDoc
    );
    setForceStayOnMedicationCard(false);
  }, [interventionId, intervention?.status, finalDocumentationSessionKey]);

  useEffect(() => {
    const doc = formRoot?.documentation;
    if (!doc || !("completeMedicationUpdate" in doc)) return;
    if (doc.completeMedicationUpdate === true) return;
    if (doc.completeMedicationUpdate === false) {
      setSessionFlag(medSavedSessionKey, false);
      setMedicationRecordSavedViaSlider(false);
    }
  }, [formRoot?.documentation, medSavedSessionKey, isSessionStorageAvailable]);

  const billingResult = formRoot?.billing?.resultAction;
  const hasBillingResultAction = Boolean(
    billingResult && String(billingResult).trim() !== ""
  );

  const currentStatus = intervention?.status;
  const isWaitingProviderResponse =
    currentStatus === InterventionStatus.WaitingProviderResponse;
  const isInterventionCompleted =
    currentStatus === InterventionStatus.Completed;

  let shouldShowDocumentationForms = true;
  if (hasBillingResultAction && billingResult === "UpdateMedicationRecord") {
    shouldShowDocumentationForms = isInterventionCompleted && isMedicationUpdateComplete
      ? true
      : isMedicationUpdateComplete && releasedToDocumentationForms;
  }

  const medicationStepComplete =
    isMedicationUpdateComplete &&
    !medicationCardEditSession &&
    medicationRecordSavedViaSlider;
  const profileAnalysisComplete = profileAnalysisStatus === "completed";

  const showDocumentationFormsUI =
    shouldShowDocumentationForms &&
    !showMedicationSlider &&
    !medicationCardEditSession &&
    !forceStayOnMedicationCard;

  const isNoUpdatePath = billingResult === "NoUpdate";
  const isUpdateMedicationPath =
    billingResult === "UpdateMedicationRecord" ||
    billingResult == null ||
    billingResult === "";
  const showFooterNextButton =
    isWaitingProviderResponse ||
    isNoUpdatePath ||
    !hasBillingResultAction ||
    (isUpdateMedicationPath && releasedToDocumentationForms);
  const showCompleteInterventionAction =
    showDocumentationFormsUI &&
    (isInterventionCompleted ||
      ((openedFromBillingFinalDocumentation || showCompletedInterventionState) &&
        showFooterNextButton));
  const showViewFinalDocumentationButton =
    !showDocumentationFormsUI &&
    !showMedicationSlider &&
    billingResult === "UpdateMedicationRecord";
  const disableViewFinalDocumentationButton =
    isEditing ||
    medicationCardEditSession ||
    (showViewFinalDocumentationButton && (!medicationStepComplete || !profileAnalysisComplete));

  const medicationName =
    formRoot?.interventionDetails?.medicationName &&
    formRoot?.interventionDetails?.medicationName !== "[Name of Drug]"
      ? formRoot.interventionDetails.medicationName
      : intervention?.medicationName || "Name of Drug";

  const interventionDocumentationForm = {
    id: 1,
    name: "Intervention Documentation Form",
    enable: true,
    type: InterventionFormTypes.InterventionDocumentation,
    inActiveIcon: "/icons/intervention/forms/clinicalCommInActive.svg",
    activeIcon: "/icons/intervention/forms/clinicalCommActive.svg",
  };

  const handleUpdateMedicationRecord = () => {
    if (billingResult === "UpdateMedicationRecord") {
      setMedicationCardEditSession(true);
    }
    setIsEditing(true);
    setClickedMedication(medicationRecordForUpdate || null);
    setShowMedicationSlider(true);
  };

  const handleViewInterventionForm = () => {
    setOpenFormModal(true);
  };

  const handleMedicationFormClose = (value) => {
    setClickedMedication(null);
    setShowMedicationSlider(value);
    setIsEditing(!!value);
    if (!value) {
      setMedicationCardEditSession(false);
    }
  };

  const handleActionButtonClick = () => {
    // Show this confirmation only for AMR interventions; skip for all other intervention types.
    if (isAnnualMedicationReviewIntervention(intervention)) {
      setOpenConfirmationDialog(true);
      return;
    }
    handleConfirmedAction();
  };

  const handleConfirmedAction = async () => {
    try {
      // Billing's "Update & Save" routes back here to update the medication
      // record. Once that record is released the user belongs on Summary, which
      // owns completion - the same place the chart-created flow lands them from
      // Billing. Without this they finish on this tab and Summary is reachable
      // only by clicking the step manually.
      const summaryStep = openedFromBillingFinalDocumentation
        ? stepNumberOf(InterventionStepsEnum.Summary)
        : null;

      const assessmentData = {
        documentation: {
          completeMedicationUpdate: true,
          releasedToDocumentationForms: true,
        },
        ...(summaryStep ? { currentStep: summaryStep } : {}),
      };

      await dispatch(
        updateInterventionAssessment(interventionId, assessmentData)
      );

      setForceStayOnMedicationCard(false);
      setReleasedToDocumentationForms(true);
      setMedicationCardEditSession(false);
      if (intervention?.patientId && interventionId) {
        await dispatch(
          getInterventionById(intervention.patientId, interventionId)
        );
      }
      setOpenConfirmationDialog(false);

      if (summaryStep) {
        setSessionFlag(finalDocumentationSessionKey, false);
        setOpenedFromBillingFinalDocumentation(false);
        onNavigate?.(summaryStep);
      }
    } catch (error) {
      console.error("Failed to update intervention assessment:", error);
      setOpenConfirmationDialog(false);
    }
  };

  const handleNextClick = async () => {
    try {
      // Persist first; only move to the next tab once the save succeeds.
      const result = await dispatch(
        updateInterventionAssessment(interventionId, {
          currentStep: nextStep,
        })
      );
      if (result) {
        onNavigate?.(nextStep);
      }
    } catch (error) {
      console.error("Failed to update intervention step:", error);
    }
  };

  const handleCompleteInterventionClick = () => {
    setShowCompletedInterventionState(true);

    setSessionFlag(finalDocumentationSessionKey, false);
    setOpenedFromBillingFinalDocumentation(false);

    if (typeof window !== "undefined" && intervention?.patientId && interventionId) {
      window.dispatchEvent(
        new CustomEvent("intervention-sidebar-focus-interventions", {
          detail: {
            patientId: intervention.patientId,
            interventionId,
          },
        })
      );
    }
  };

  const footerAction = useMemo(() => {
    if (showCompleteInterventionAction && !showCompletedInterventionState) {
      return {
        key: "completeIntervention",
        text: "Complete Intervention",
        className: "w-[270px] max-h-[52px] !rounded-[30px]",
        onClick: handleCompleteInterventionClick,
        disabled: false,
      };
    }

    if (showViewFinalDocumentationButton) {
      return {
        key: "viewFinalDocumentation",
        text: "View Final Documentation",
        className: "w-[270px] max-h-[52px] !rounded-[30px]",
        onClick: handleActionButtonClick,
        disabled: disableViewFinalDocumentationButton,
      };
    }

    if (showFooterNextButton) {
      return {
        key: "next",
        text: "Next",
        className: "w-[200px] max-h-[52px] !rounded-[30px]",
        onClick: handleNextClick,
        disabled: false,
      };
    }

    return {
      key: "viewFinalDocumentationFallback",
      text: "View Final Documentation",
      className: "w-[270px] max-h-[52px] !rounded-[30px]",
      onClick: handleActionButtonClick,
      disabled: disableViewFinalDocumentationButton,
    };
  }, [
    showCompleteInterventionAction,
    showCompletedInterventionState,
    showViewFinalDocumentationButton,
    showFooterNextButton,
    disableViewFinalDocumentationButton,
    handleCompleteInterventionClick,
    handleActionButtonClick,
    handleNextClick,
  ]);

  return (
    <>
      <div className="w-full max-h-fit overflow-auto">
        <div className="w-full h-full flex flex-col py-3">
          <div className="flex gap-8 flex-1">
            {showDocumentationFormsUI ? (
              <DocumentationFormsCard intervention={intervention} />
            ) : (
              <>
              <div className="w-[550px] rounded-[4px] border border-gray-100 bg-gray-10 flex flex-col">
                <div className="w-[550px] h-[72px] gap-[10px] opacity-1 pt-[12px] pr-[24px] pb-[12px] pl-[24px] border-t-[1px] border-t-gray-100 border-t-l-[4px] border-t-r-[4px] bg-gray-20 border border-gray-100 flex items-center justify-between">
                  <h3 className="text-teal-300 font-semibold text-lg">
                    {medicationName}
                  </h3>
                  {!isEditing && medicationStepComplete && (
                    <Menu
                      as="div"
                      className="relative flex pt-1 pr-2 justify-end"
                    >
                      <div>
                        <Menu.Button>
                          <ReactSVG
                            src="/icons/triple-dot.svg"
                            className="flex w-2 scale-x-[1.5] scale-y-[1.5]"
                          />
                        </Menu.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform translate-y-2 opacity-0 scale-95"
                        enterTo="transform translate-y-0 opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform translate-y-0 opacity-100 scale-100"
                        leaveTo="transform translate-y-2 opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute left-6 top-6 z-50 w-24 origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-opacity-5">
                          <Menu.Item>
                            <button
                              className="block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-100"
                              onClick={() => {
                                setIsEditing(true);
                                handleUpdateMedicationRecord();
                              }}
                            >
                              Edit
                            </button>
                          </Menu.Item>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  )}
                </div>
                <div className="flex-1 p-6">
                  {/* Date row */}
                  <div className="rounded border border-gray-100 bg-white flex items-center justify-between px-4 py-4 mb-4">
                    <span className="text-gray-900 font-semibold">
                      Intervention Date
                    </span>
                    <span className="text-gray-700 font-medium">
                      {formatDateToDD_MMM_YYYY(lastModified)}
                    </span>
                  </div>

                  {/* Checklist rows */}
                  <div className="flex flex-col gap-4">
                    <div className="rounded border border-gray-100 bg-white flex items-center gap-3 px-4 py-4">
                      <ReactSVG
                        src="/icons/intervention/Checkbox.svg"
                        className="w-5 h-5"
                      />
                      <span className="text-gray-800 font-medium">
                        Review Completed
                      </span>
                    </div>
                    <div className="rounded border border-gray-100 bg-white flex items-center gap-3 px-4 py-4">
                      <ReactSVG
                        src="/icons/intervention/Checkbox.svg"
                        className="w-5 h-5"
                      />
                      <span className="text-gray-800 font-medium">
                        Intervention Created
                      </span>
                    </div>
                    <div className="rounded border border-gray-100 bg-white flex items-center gap-3 px-4 py-4">
                      <ReactSVG
                        src="/icons/intervention/Checkbox.svg"
                        className="w-5 h-5"
                      />
                      <span className="text-gray-800 font-medium">
                        Provider Response Received
                      </span>
                    </div>

                    {/* Open the original intervention form */}
                    <button
                      type="button"
                      className={`text-left rounded border border-gray-100 flex items-center gap-3 px-4 py-4 bg-yellow-50`}
                      onClick={handleViewInterventionForm}
                    >
                      <ReactSVG
                        src="/icons/intervention/refresh.svg"
                        className="w-5 h-5"
                      />
                      <span className="text-gray-800 font-medium">
                        Reopen Original Intervention Form
                      </span>
                      <span className="ml-auto flex items-center mr-4 -mt-4">
                      <ReactSVG
                        src={interventionDocumentationForm.inActiveIcon}
                        className="w-6 h-6 shrink-0"
                      />
                      </span>
                    </button>

                    {/* Placeholder button (feature coming soon) */}
                    <button
                      type="button"
                      className={`text-left rounded border border-gray-100 flex items-center gap-3 px-4 py-4 bg-yellow-50`}
                      data-tooltip-id={comingSoonReturnedFormTooltipId}
                      onClick={() => {
                        setComingSoonTooltipOpen(true);
                      }}
                      onMouseEnter={() => setComingSoonTooltipOpen(true)}
                      onMouseLeave={() => setComingSoonTooltipOpen(false)}
                    >
                      <ReactSVG
                        src="/icons/intervention/refresh.svg"
                        className="w-5 h-5"
                      />
                      <span className="text-gray-800 font-medium">
                        Reopen Returned Intervention Form
                      </span>
                      <span className="ml-auto flex items-center mr-4 -mt-4">
                        <ReactSVG
                          src={interventionDocumentationForm.inActiveIcon}
                          className="w-6 h-6 shrink-0"
                        />
                      </span>
                    </button>

                    <NewToolTip
                      id={comingSoonReturnedFormTooltipId}
                      isOpen={comingSoonTooltipOpen}
                      render={() => (
                        <div className="bg-white text-sm text-gray-700 px-3 py-1">
                          Coming soon
                        </div>
                      )}
                    />
                  </div>

                  {/* Footer action */}
                  <div className="flex items-center justify-end mt-10">
                    {!medicationStepComplete ? (
                      <Button
                        type="primary"
                        className="!rounded-[6px] w-full"
                        text="Update Medication Record"
                        onClick={handleUpdateMedicationRecord}
                      />
                    ) : (
                      <div className="flex items-center gap-3 px-6 py-6 text-REELTeal font-bold">
                        <span className="mt-3">Complete</span>
                        <ReactSVG
                          src="/icons/medication-card-complete.svg"
                          className="w-8 h-8"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {medicationStepComplete && (
                <div className="h-[680px] w-[520px] rounded-[4px] flex flex-col overflow-hidden border border-gray-100 bg-white">
                  <div className="h-[440px] w-[440px] rounded-full mx-auto my-8 bg-gray-50 bg-opacity-40 flex items-center justify-center relative">
                    <ReactSVG
                      src="/icons/profile-analysis-p1.svg"
                      className="absolute left-[28%] top-[20%]"
                    />
                    <ReactSVG
                      src="/icons/profile-analysis-p2.svg"
                      className="absolute top-[25%] left-[41%]"
                    />
                    <p className="absolute bottom-[10%] left-24 text-teal-600 text-xl font-normal">
                      Powered by RxVigilance
                    </p>
                  </div>
                  <div className="mx-7 mt-20 mb-5">
                    {profileAnalysisStatus !== "completed" ? (
                      <Button
                        className="w-full !px-2 !text-xl"
                        type="primary"
                        text="Profile Analysis"
                        onClick={() => {
                          setProfileAnalysispopup(true);
                        }}
                      />
                    ) : (
                      <h4 className="text-xl text-REELTeal font-bold items-center justify-center flex">
                        Profile Analysis Coming Soon!
                      </h4>
                    )}
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        </div>

        {/* MedicationCreate Modal */}
        <MedicationCreate
          create={!interventionMedicationId}
          patientId={intervention?.patientId}
          type="Medication"
          open={showMedicationSlider}
          fullMedication={clickedMedication ?? medicationRecordForUpdate}
          step={1}
          maxSteps={1}
          isIntervention={true}
          onClose={handleMedicationFormClose}
          onSave={async () => {
            if (billingResult === "UpdateMedicationRecord") {
              await dispatch(
                updateInterventionAssessment(interventionId, {
                  documentation: {
                    completeMedicationUpdate: true,
                    releasedToDocumentationForms: false,
                  },
                })
              );
            }

            setSessionFlag(medSavedSessionKey, true);
            setMedicationRecordSavedViaSlider(true);

            // Keep user on medication card until explicit final-doc action.
            setForceStayOnMedicationCard(true);
            setMedicationCardEditSession(false);
            setShowMedicationSlider(false);
            setIsEditing(false);
            if (intervention?.patientId && interventionId) {
              await dispatch(
                getInterventionById(intervention.patientId, interventionId)
              );
            }

            if (
              typeof window !== "undefined" &&
              intervention?.patientId &&
              interventionId
            ) {
              window.dispatchEvent(
                new CustomEvent("intervention-sidebar-focus-medications", {
                  detail: {
                    patientId: intervention.patientId,
                    interventionId,
                  },
                })
              );
            }
          }}
        />

        {/* Intervention form modal (original form view) */}
        <ConfirmationModalNew
          open={openFormModal}
          handleClose={() => setOpenFormModal(false)}
          onCross={() => setOpenFormModal(false)}
          hideActions={true}
          setWideModal={true}
          className="!min-w-[1000px] !w-[1000px]"
          title={interventionDocumentationForm.name}
          content={() => (
            <InterventionForm
              intervention={intervention}
              formType={interventionDocumentationForm.type}
              hideIconButton={true}
              onBack={() => setOpenFormModal(false)}
            />
          )}
        />

        <ConfirmationModalNew
          className="!min-w-[520px]"
          type="primary"
          title="Powered by RxVigilance - Coming Soon!"
          content={() => (
            <div className="flex flex-col gap-4">
              <p className="text-body-md text-gray-600 font-medium">
                Profile analysis tool can be used to generate numerous
                alerts to identify potential problems.
              </p>
            </div>
          )}
          confirmText={"Continue to Review"}
          confirmAction={() => {
            setProfileAnalysispopup(false);
            setProfileAnalysisStatus("completed");
            setSessionFlag(profileAnalysisSessionKey, true);
          }}
          open={profileAnalysispopup}
          handleClose={() => {
            setProfileAnalysispopup(false);
          }}
          onCross={() => {
            setProfileAnalysispopup(false);
          }}
        />
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between space-x-4">
        <div className="w-full">{/* Progress indicator could go here */}</div>
        <div className="flex items-center gap-3">
          {showCompleteInterventionAction && showCompletedInterventionState ? (
            <div className="flex items-center gap-3 px-6 py-6 text-REELTeal font-bold text-[20px]">
              <span className="mt-4">Completed</span>
              <ReactSVG
                src="/icons/medication-card-complete.svg"
                className="w-8 h-8"
              />
            </div>
          ) : (
            <Button
              type="primary"
              text={footerAction.text}
              className={footerAction.className}
              onClick={footerAction.onClick}
              disabled={footerAction.disabled}
            />
          )}
        </div>
      </div>

      {/* Confirmation Dialog for View Final Documentation */}
      <ConfirmationModalNew
        open={openConfirmationDialog}
        title="Confirm Medication Record Update"
        className="!min-w-[630px]"
        confirmText="Go to Documentation"
        cancelText="Go to interventions"
        content={() => (
          <div>
            <div className="flex font-semibold text-teal-300 text-xl mb-6">
              Confirm All Drug Therapy Problems Are Resolved
            </div>
            <div className="flex font-regular text-gray-800 text-base mb-4">
              Please confirm that all identified Drug Therapy Problems have been addressed before finalizing the medication record.
            </div>
          </div>
        )}
        onCross={() => setOpenConfirmationDialog(false)}
        handleClose={() => setOpenConfirmationDialog(false)}
        cancelAction={() => {
          setOpenConfirmationDialog(false);
          navigate(`/patient/${intervention?.patientId}/interventions`, {
            replace: true,
            state: { selectedCategory: "interventions" },
          });
        }}
        confirmAction={handleConfirmedAction}
      />
    </>
  );
};

export default Documentation;
